/**
 * Voice limiter for combat audio (pure logic, no WebAudio). A big trash pull can produce dozens
 * of impacts per frame; the limiter keeps the mix readable and the node count bounded:
 *  - the same cue retriggered within `minGapS` is merged (dropped: the first voice already plays);
 *  - each cue has at most `perCue` live voices (the oldest is stolen for an equal/higher priority);
 *  - at most `maxVoices` overall: the lowest-priority, oldest voice is stolen when a new voice
 *    outranks or equals it, otherwise the new voice is rejected.
 * Fixed-size arrays, no allocation per call.
 */

export interface AdmitResult {
  /** Voice id to use for the new sound, or -1 when rejected. */
  id: number;
  /** Voice id the engine must stop (fade out) to make room, or -1. */
  stolen: number;
}

export class VoiceLimiter {
  private readonly cue: (string | null)[];
  private readonly prio: Float32Array;
  private readonly start: Float64Array;
  private readonly end: Float64Array;
  private readonly ids: Int32Array;
  private nextId = 1;
  private readonly out: AdmitResult = { id: -1, stolen: -1 };

  constructor(
    readonly maxVoices = 24,
    readonly perCue = 3,
    readonly minGapS = 0.03,
  ) {
    this.cue = new Array<string | null>(maxVoices).fill(null);
    this.prio = new Float32Array(maxVoices);
    this.start = new Float64Array(maxVoices);
    this.end = new Float64Array(maxVoices);
    this.ids = new Int32Array(maxVoices);
  }

  /** Live voices at `now` (seconds). */
  live(now: number): number {
    let n = 0;
    for (let i = 0; i < this.maxVoices; i++) if (this.cue[i] !== null && this.end[i]! > now) n++;
    return n;
  }

  /**
   * Ask for a voice for `cue` (priority: higher survives) lasting `durS` from `now`, with at most
   * `perCue` live voices of that cue. The result is a shared scratch object: read it before the next call.
   */
  admit(cue: string, priority: number, now: number, durS: number, perCue = this.perCue): AdmitResult {
    const out = this.out;
    out.id = -1;
    out.stolen = -1;
    let free = -1;
    let sameN = 0;
    let sameOldest = -1;
    let victim = -1;
    for (let i = 0; i < this.maxVoices; i++) {
      const c = this.cue[i];
      if (c === null || this.end[i]! <= now) {
        this.cue[i] = null;
        if (free < 0) free = i;
        continue;
      }
      if (c === cue) {
        if (now - this.start[i]! < this.minGapS) return out; // merged into the voice that just started
        sameN++;
        if (sameOldest < 0 || this.start[i]! < this.start[sameOldest]!) sameOldest = i;
      }
      // Global victim: lowest priority, then oldest.
      if (victim < 0 || this.prio[i]! < this.prio[victim]! || (this.prio[i] === this.prio[victim] && this.start[i]! < this.start[victim]!)) victim = i;
    }
    let slot = -1;
    if (sameN >= Math.max(1, perCue)) {
      if (this.prio[sameOldest]! > priority) return out;
      slot = sameOldest;
    } else if (free >= 0) slot = free;
    else {
      if (victim < 0 || this.prio[victim]! > priority) return out;
      slot = victim;
    }
    if (this.cue[slot] !== null && this.end[slot]! > now) out.stolen = this.ids[slot]!;
    const id = this.nextId++;
    this.cue[slot] = cue;
    this.prio[slot] = priority;
    this.start[slot] = now;
    this.end[slot] = now + Math.max(0.01, durS);
    this.ids[slot] = id;
    out.id = id;
    return out;
  }

  clear(): void {
    this.cue.fill(null);
  }
}
